import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ListTodo,
  Users,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  X,
  Lock,
  Loader2,
  Eye,
  EyeOff,
  CheckCircle2,
  Check,
  Circle,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/logo-blanco-horizontal-etc.png";

const PASSWORD_RULES = [
  { id: "length", label: "Mínimo 8 caracteres", test: (pw) => pw.length >= 8 },
  { id: "upper", label: "Una mayúscula", test: (pw) => /[A-Z]/.test(pw) },
  { id: "lower", label: "Una minúscula", test: (pw) => /[a-z]/.test(pw) },
  {
    id: "special",
    label: "Un número o símbolo",
    test: (pw) => /[0-9!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(pw),
  },
];

function AdminPasswordDrawer({ isOpen, onClose }) {
  const { updatePassword } = useAuth();
  const [visible, setVisible] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      requestAnimationFrame(() => setVisible(true));
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      setPw("");
      setConfirm("");
      setError(null);
      setSuccess(false);
      onClose();
    }, 200);
  };

  const passedRules = PASSWORD_RULES.filter((r) => r.test(pw));
  const allRulesPassed = passedRules.length === PASSWORD_RULES.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!allRulesPassed) {
      setError("La contraseña no cumple todos los requisitos");
      return;
    }
    if (pw !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }

    setLoading(true);
    try {
      await updatePassword(pw);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const content = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-slate-900">
        <h3 className="font-semibold text-white flex items-center gap-2">
          <Lock size={18} />
          {success ? "Contraseña actualizada" : "Cambiar Contraseña"}
        </h3>
        <button
          onClick={handleClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 bg-white">
        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              ¡Contraseña actualizada!
            </h4>
            <p className="text-sm text-gray-600 mb-6">
              Tu contraseña se ha actualizado correctamente.
            </p>
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Nueva contraseña
              </label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Introduce tu nueva contraseña"
                  className="w-full px-4 py-3 pr-10 border-[1.5px] border-gray-200 rounded-xl text-sm bg-gray-50 outline-none transition-all focus:border-indigo-500 focus:bg-white focus:ring-3 focus:ring-indigo-500/10"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {pw.length > 0 && (
                <div className="mt-3 p-3 bg-gray-50 rounded-xl space-y-2">
                  {PASSWORD_RULES.map((rule) => {
                    const passed = rule.test(pw);
                    return (
                      <div
                        key={rule.id}
                        className={`flex items-center gap-2 text-sm transition-colors ${
                          passed ? "text-emerald-600" : "text-gray-400"
                        }`}
                      >
                        {passed ? (
                          <Check size={14} className="shrink-0" />
                        ) : (
                          <Circle size={14} className="shrink-0" />
                        )}
                        {rule.label}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirmar contraseña
              </label>
              <input
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Repite la contraseña"
                className="w-full px-4 py-3 border-[1.5px] border-gray-200 rounded-xl text-sm bg-gray-50 outline-none transition-all focus:border-indigo-500 focus:bg-white focus:ring-3 focus:ring-indigo-500/10"
              />
              {confirm.length > 0 && pw !== confirm && (
                <p className="mt-2 text-sm text-amber-600">
                  Las contraseñas no coinciden
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !allRulesPassed || pw !== confirm}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar contraseña"
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  const drawer = (
    <div className="fixed inset-0 z-[99999]">
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-200 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-md
          flex-col bg-white shadow-2xl
          transition-transform duration-200 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {content}
      </div>

      <div
        className={`
          md:hidden fixed inset-0 bg-white
          flex flex-col
          transition-transform duration-200 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
        `}
      >
        {content}
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}

const NAV_ITEMS = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  {
    to: "/admin/actividades",
    icon: ListTodo,
    label: "Actividades",
    superAdminOnly: true,
  },
  { to: "/admin/alumnos", icon: Users, label: "Alumnos" },
  {
    to: "/admin/informes",
    icon: FileText,
    label: "Informes de respuestas",
  },
  {
    to: "/admin/configuracion",
    icon: Settings,
    label: "Configuración",
    superAdminOnly: true,
  },
];

export default function AdminSidebar({ isOpen, onToggle }) {
  const { user, signOut, userType, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [showPasswordDrawer, setShowPasswordDrawer] = useState(false);

  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.superAdminOnly || isSuperAdmin
  );

  const userName = user?.user_metadata?.name || user?.email || "Admin";
  const initials = userName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleSignOut = async () => {
    navigate("/", { replace: true });
    await signOut();
  };

  const goToPortal = () => {
    navigate("/");
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen w-64 bg-slate-900 flex flex-col z-50 transition-transform lg:translate-x-0 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="px-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Entrena Tu Centro" className="h-18" />
            <span className="text-xs font-semibold text-indigo-400 bg-indigo-500/20 px-2 py-0.5 rounded">
              ADMIN
            </span>
          </div>
          <button
            onClick={onToggle}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleNavItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={() => {
                if (window.innerWidth < 1024) onToggle();
              }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-indigo-500/20 text-indigo-400"
                    : "text-gray-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          {userType === "student" && (
            <button
              onClick={goToPortal}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-400 hover:bg-white/5 hover:text-white rounded-lg transition-all text-sm font-medium"
            >
              <ChevronLeft size={18} />
              <span>Ir al Portal</span>
            </button>
          )}
          <button
            onClick={() => setShowPasswordDrawer(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-400 hover:bg-white/5 hover:text-white rounded-lg transition-all text-sm font-medium"
          >
            <Lock size={18} />
            <span>Cambiar Contraseña</span>
          </button>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-red-400 hover:bg-red-500/10 rounded-lg transition-all text-sm font-medium"
          >
            <LogOut size={18} />
            <span>Cerrar Sesión</span>
          </button>
        </div>

        <div className="px-5 py-4 border-t border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {userName}
            </p>
            <p className="text-[0.7rem] text-gray-400">
              {isSuperAdmin ? "Super Administrador" : "Administrador"}
            </p>
          </div>
        </div>
      </aside>

      <AdminPasswordDrawer
        isOpen={showPasswordDrawer}
        onClose={() => setShowPasswordDrawer(false)}
      />
    </>
  );
}

export function AdminMenuButton({ onToggle }) {
  return (
    <button
      onClick={onToggle}
      className="lg:hidden w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:border-gray-300 transition-all"
    >
      <Menu size={18} />
    </button>
  );
}
